
import * as am4core from "@amcharts/amcharts4/core"
import * as am4charts from "@amcharts/amcharts4/charts"
import Component from "vue-class-component"
import Vue from "vue"
import {Prop} from "vue-property-decorator"
import {chartTheme} from "@/ui/chartTheme"
import {theme} from "@/theme"
import {MemberSnapshotDimension, MemberSnapshotMeasure} from "@/cube"
import {MetricSliced, MetricStore} from "@/cube/metric-data/MetricStore"
import {removeDimensionSortPrefix} from "@/cube/removeDimensionSortPrefix"
import {keyedDataUpdate} from "@/cube/keyedDataUpdate"
import {toKey} from "@/cube/toKey"
import {QueryData} from "@/cube/cubejsApi"
import {dropShadowFilter} from "@/ui/chart/dropShadowFilter"
import NoDataMessage from "@/ui/NoDataMessage.vue"

am4core.useTheme(chartTheme)

@Component({
  components: {NoDataMessage},
})
export default class InvestmentApproachChart extends Vue {
  @Prop()
  metricStore: MetricStore

  private hasData = true
  private ready = false
  private chart: am4charts.XYChart | undefined

  mounted() {
    const metricData = this.metricStore.getSliced(
      MemberSnapshotMeasure.countMembers,
      [MemberSnapshotDimension.riskProfileName]
    )

    this.$watch(
      () => metricData.end,
      () => {
        if (metricData.end) {
          if (this.chart === undefined) {
            this.chart = this.createChart()
          }
          this.hasData = metricData.end.length > 0
          if (this.hasData) {
            this.chart.show()
            this.updateChartData(metricData)
            this.ready = true
          } else {
            this.chart.hide()
          }
        }
      },
      {immediate: true}
    )
  }

  beforeDestroy() {
    this.chart?.dispose()
  }

  updateChartData(
    metricSliced: MetricSliced<MemberSnapshotMeasure.countMembers>
  ) {
    console.log("Updating data", metricSliced.end)

    metricSliced.end!.forEach(row => {
      row[MemberSnapshotDimension.riskProfileName] = removeDimensionSortPrefix(
        row[MemberSnapshotDimension.riskProfileName]
      )
    })

    keyedDataUpdate(
      MemberSnapshotMeasure.countMembers,
      (row: any) => toKey(row, [MemberSnapshotDimension.riskProfileName]),
      <QueryData>metricSliced.end,
      this.chart!.data
    )

    this.chart?.invalidateRawData()
  }

  private createChart() {
    const chart = am4core.create((<any>this.$refs).chart, am4charts.XYChart)

    chart.data = [
      ["Cash", "#a7c8ea"],
      ["Cautious", "#ecac7f"],
      ["Conservative", "#00325b"],
      ["Moderate", "#8a941e"],
      ["Assertive", "#4e87c6"],
      ["Aggressive", "#bb692e"],
    ].map(([riskProfileName, color]) => ({
      [MemberSnapshotDimension.riskProfileName]: riskProfileName,
      [MemberSnapshotMeasure.countMembers]: 0,
      color,
    }))

    const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis())
    categoryAxis.dataFields.category = MemberSnapshotDimension.riskProfileName
    categoryAxis.title.text = "Risk profiles"
    categoryAxis.title.marginTop = 20
    categoryAxis.title.fontSize = 14
    categoryAxis.renderer.minGridDistance = 4
    categoryAxis.renderer.inside = true
    categoryAxis.renderer.grid.template.location = 0
    categoryAxis.renderer.grid.template.disabled = true

    let categoryAxisLabels = categoryAxis.renderer.labels.template
    categoryAxisLabels.rotation = -90
    categoryAxisLabels.horizontalCenter = "left"
    categoryAxisLabels.verticalCenter = "middle"
    categoryAxisLabels.inside = false
    categoryAxisLabels.fontSize = 22
    categoryAxisLabels.fontWeight = "bold"
    categoryAxisLabels.fill = am4core.color(theme().semiMutedColor)
    categoryAxisLabels.fillOpacity = 0.6

    const valueAxis = chart.yAxes.push(new am4charts.ValueAxis())
    valueAxis.title.text = "% Members"
    valueAxis.title.marginRight = 7
    valueAxis.title.fontSize = 14
    valueAxis.strictMinMax = true
    valueAxis.max = 100
    valueAxis.min = 0

    const series = chart.series.push(new am4charts.ColumnSeries())
    series.calculatePercent = true
    series.dataFields.valueY = MemberSnapshotMeasure.countMembers
    series.dataFields.valueYShow = "percent"
    series.dataFields.categoryX = MemberSnapshotDimension.riskProfileName

    series.columns.template.propertyFields.fill = "color"
    series.columns.template.propertyFields.stroke = "color"
    series.columns.template.tooltipText =
      "{categoryX}: {valueY} members ({valueY.percent.formatNumber('#.#')}%)"
    series.columns.template.adapter.add("tooltipText", (text, target) =>
      (<am4charts.ColumnSeriesDataItem>target.dataItem).valueY === 1
        ? text?.replace("members", "member")
        : text
    )
    series.strokeWidth = 2
    series.stroke = am4core.color("white")
    series.fillOpacity = 0.35
    series.filters.push(dropShadowFilter())

    return chart
  }
}
